import React, { useEffect, useState } from "react";

import { minLength, maxLength, regex } from "react-admin";

import { Grid } from "@material-ui/core";

import { CountryList, REGEX_PHONE } from "../../utils/constants";
import BoundedSelectInput from "./BoundedSelectInput";
import BoundedTextInput from "./BoundedTextInput";
import { isEmptyAddress } from "../../utils/utils";

// Function to access nested property or array index based on the source
const getValueFromSource = (source, allValues) => {
  if (source.includes("[")) {
    // Handle array-style source like addressBook[1]
    const arrayKey = source.match(/[^[\]]+/g); // Extract parts of the path
    if (arrayKey && arrayKey.length >= 2) {
      const objectKey = arrayKey[0]; // 'addressBook'
      const index = parseInt(arrayKey[1], 10); // Index, e.g., '1'
      return allValues[objectKey]?.[index]; // Safely access the array item
    }
  } else {
    // Handle direct property access like account.address
    return allValues[source];
  }
  return undefined;
};

const validateAddress = (value, allValues, source, required) => {
  // Get the corresponding value from allValues based on the source
  const sourceValue = getValueFromSource(source, allValues);

  // Perform validation
  const retval =
    (!isEmptyAddress(sourceValue) && !value) || (required && !value)
      ? "Required"
      : undefined;
  return retval;
};

const validateState = (source, required) => [
  minLength(2, "2 Letter"),
  maxLength(2),
  (value, allValues) => validateAddress(value, allValues, source, required),
];
const validateZip = (source, required) => [
  minLength(5),
  (value, allValues) => validateAddress(value, allValues, source, required),
];

const validatePhone = () => [regex(REGEX_PHONE, "Must be valid Phone No.")];

export const AddressInput = (props) => {
  const {
    source,
    allValues,
    showBusinessName = false,
    showPhone = false,
    required,
    ...rest
  } = props;
  const truncatedSource = source.endsWith(".") ? source.slice(0, -1) : source;
  const sourceValue = getValueFromSource(truncatedSource, allValues);

  // When the fields are disabled (an address-book entry is selected, or the
  // shipment is released) the user cannot edit them, so their validators must
  // not block submit. A selected saved address is complete on the
  // schema-required fields by construction; only the optional `name` can be
  // absent, which would otherwise mark the disabled Business Name field
  // "Required" and deadlock the form. Suppress validation while disabled.
  const isDisabled = Boolean(rest.disabled);

  const [isRequired, setIsRequired] = useState(
    !isEmptyAddress(sourceValue) || required,
  );

  useEffect(() => {
    setIsRequired(!isEmptyAddress(sourceValue) || required);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceValue]);

  return (
    <Grid container direction="row" spacing={1}>
      {showBusinessName && (
        <Grid item xs={6}>
          <BoundedTextInput
            source={`${truncatedSource}.name`}
            label="Business Name"
            validate={
              isDisabled
                ? undefined
                : (value, allValues) =>
                    validateAddress(value, allValues, truncatedSource, required)
            }
            isRequired={isRequired}
            {...rest}
          />
        </Grid>
      )}
      <Grid item xs={showBusinessName ? 6 : 12}>
        <BoundedTextInput
          source={`${truncatedSource}.street`}
          label="Street Address"
          validate={
            isDisabled
              ? undefined
              : (value, allValues) =>
                  validateAddress(value, allValues, truncatedSource, required)
          }
          isRequired={isRequired}
          {...rest}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <BoundedTextInput
          source={`${truncatedSource}.city`}
          label="City"
          validate={
            isDisabled
              ? undefined
              : (value, allValues) =>
                  validateAddress(value, allValues, truncatedSource, required)
          }
          isRequired={isRequired}
          {...rest}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <Grid container spacing={1} direction="row">
          <Grid item xs={4}>
            <BoundedTextInput
              source={`${truncatedSource}.state`}
              label="State"
              validate={
                isDisabled ? undefined : validateState(truncatedSource, required)
              }
              isRequired={isRequired}
              inputProps={{
                maxLength: 2,
                style: { textTransform: "uppercase" },
              }}
              {...rest}
            />
          </Grid>
          <Grid item xs={8}>
            <BoundedTextInput
              source={`${truncatedSource}.zipCode`}
              label="Zip Code"
              validate={
                isDisabled ? undefined : validateZip(truncatedSource, required)
              }
              isRequired={isRequired}
              inputProps={{ style: { textTransform: "uppercase" } }}
              {...rest}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={3}>
        <BoundedSelectInput
          source={`${truncatedSource}.country`}
          label="Country"
          choices={CountryList}
          allowEmpty={true}
          isRequired={isRequired}
          validate={
            isDisabled
              ? undefined
              : (value, allValues) =>
                  validateAddress(value, allValues, truncatedSource, required)
          }
          {...rest}
        />
      </Grid>
      {showPhone && (
        <Grid item xs={12} md={4}>
          <BoundedTextInput
            source={`${truncatedSource}.phone`}
            label="Phone"
            validate={isDisabled ? undefined : validatePhone(truncatedSource, required)}
            {...rest}
          />
        </Grid>
      )}
    </Grid>
  );
};
